import React, { useEffect, useState } from "react";
import { WaitingNotice } from "@/components/FindoSpinner";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { labelFor } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import PhotoGallery from "@/components/PhotoGallery";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerificationAnswersForm } from "@/components/Verification";
import {
  PackageSearch, Store, CalendarDays, Tag, Palette, Sparkles, BadgeCheck,
  ArrowRight, ArrowLeft, XCircle, CheckCircle2, ShieldCheck,
} from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const REJECT_REASONS = ["Mauvaise couleur", "Pas le bon modèle", "Ce n'est pas le mien", "Autre"];
const REASON_KEYS = { "Mauvaise couleur": "hc.wrongColor", "Pas le bon modèle": "hc.wrongModel", "Ce n'est pas le mien": "hc.notMine", "Autre": "hc.other" };
const DONE_STATUSES = ["paid", "shipping_requested", "shipping_paid", "shipped", "restituted", "delivered"];

export default function Verifier() {
  const { declarationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPay = searchParams.get("from") === "pay";
  const { t } = useLang();
  const [decl, setDecl] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [showReasons, setShowReasons] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(null); // null | {rematched: bool}
  const [showOther, setShowOther] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const vstatus = decl?.verification_blocked ? "rejected" : (decl?.verification_status || "none");

  const refetch = () =>
    api(`/declarations/${declarationId}/public`, { auth: false }).then(setDecl).catch(() => {});

  useEffect(() => {
    api(`/declarations/${declarationId}/public`, { auth: false })
      .then((d) => { setDecl(d); setState("ready"); })
      .catch(() => setState("notfound"));
  }, [declarationId]);

  // While a verification is pending partner approval, poll so the page advances
  // automatically the moment the partner approves.
  useEffect(() => {
    if (vstatus !== "pending") return;
    const t = setInterval(refetch, 12000);
    return () => clearInterval(t);
  }, [vstatus, declarationId]);

  // Owner submits identity-verification characteristics (blocking: partner must
  // approve before payment). Mirrors the cross-flow verification step.
  async function submitVerification(answers) {
    setVerifying(true);
    try {
      await api(`/declarations/${declarationId}/verify-claim`, { method: "POST", auth: false, body: { answers } });
      setVerifyOpen(false);
      await refetch();
      toast.success(t("hc.verificationSentToThePartner"));
    } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  }

  // Verification approved → persist identity confirmation and go to payment.
  async function proceedToPay() {
    setConfirming(true);
    try {
      await api(`/declarations/${declarationId}/confirm-identity`, { method: "POST", auth: false });
    } catch (e) { /* non-blocking */ }
    navigate(`/pay/${declarationId}`);
  }

  async function rejectMine(reason) {
    setRejecting(true);
    try {
      const res = await api(`/declarations/${declarationId}/reject-match`, {
        method: "POST", auth: false, body: { reason: reason || null },
      });
      setRejected({ rematched: !!res.rematched });
    } catch (e) {
      toast.error(e.message || "Une erreur est survenue");
    } finally {
      setRejecting(false);
    }
  }

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(document.documentElement.lang || "fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };

  if (state === "loading") {
    return <div className="grid place-items-center py-32 text-findo-navy/60" data-testid="verifier-loading"><FindoSpinner size={28} className="mb-3" /> {t("c.loading")}</div>;
  }
  if (state === "notfound" || !decl) {
    return (
      <div className="max-w-md mx-auto py-28 text-center" data-testid="verifier-notfound">
        <PackageSearch size={40} className="mx-auto text-slate-300" />
        <p className="mt-4 text-slate-500">{t("hc.declarationNotFound")}</p>
        <Link to="/mon-compte"><Button variant="outline" className="mt-6 rounded-full">{t("hc.myAccount")}</Button></Link>
      </div>
    );
  }

  const item = decl.item_preview;
  const alreadyPaid = DONE_STATUSES.includes(decl.status);

  // Rejection outcome screen
  if (rejected) {
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="verifier-rejected">
        <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-emerald-100 text-emerald-600"><CheckCircle2 size={30} /></div>
        {rejected.rematched ? (
          <>
            <h1 className="mt-5 font-display font-bold text-2xl text-findo-navy">{t("hc.aNewMatchHasBeen")}</h1>
            <p className="mt-2 text-slate-500 text-sm">{t("hc.weRanTheAnalysisAgain")}</p>
            <Button onClick={() => { setRejected(null); setShowReasons(false); window.location.reload(); }} className="mt-6 rounded-full font-semibold bg-findo-danger hover:bg-findo-danger/90" data-testid="verifier-recheck-btn">
              {t("hc.checkTheNewItem")} <ArrowRight size={17} className="ml-1.5" />
            </Button>
          </>
        ) : (
          <>
            <h1 className="mt-5 font-display font-bold text-2xl text-findo-navy">{t("hc.thankYouForYourFeedback")}</h1>
            <p className="mt-2 text-slate-500 text-sm">{t("hc.weHaveInformedTheEstablishment")}</p>
            <Button onClick={() => navigate("/mon-compte")} className="mt-6 rounded-full font-semibold" variant="outline" data-testid="verifier-rejected-back">{t("hc.backToMyAccount")}</Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-background min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="text-sm font-semibold text-findo-red mb-1 flex items-center gap-1.5" data-testid="verifier-eyebrow"><PackageSearch size={16} /> {t("hc.itemFoundAt")} {decl.partner_name}</p>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-findo-navy mb-2" data-testid="verifier-title">{t("hc.isThisReallyYourItem")}</h1>
        <p className="text-slate-500 mb-8 text-sm">{t("hc.checkThePhotosAndCharacteristics")}</p>

        {!decl.matched || !item ? (
          <div className="bg-white rounded-2xl border border-border p-8 text-center card-shadow" data-testid="verifier-nomatch">
            <PackageSearch className="mx-auto text-findo-navy/40 mb-3" size={40} />
            <p className="text-slate-500 text-sm">{t("hc.noMatchIsAvailableFor")}</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-border overflow-hidden card-shadow" data-testid="verifier-card">
              {(item.image_base64 || item.image_base64_2) && (
                <div className="h-72 bg-slate-100">
                  <PhotoGallery photos={[item.image_base64, item.image_base64_2]} alt={item.description} fill testid="verifier-gallery" />
                </div>
              )}
              <div className="p-6 space-y-5">
                <div className="flex items-start gap-3" data-testid="verifier-description">
                  <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy shrink-0"><PackageSearch size={18} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Description</p>
                    <p className="text-findo-navy font-medium">{item.description || decl.description || "—"}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Detail icon={<Tag size={16} />} label={t("hc.category")} value={labelFor(decl.category)} testid="verifier-category" />
                  <Detail icon={<Store size={16} />} label={t("hc.establishment")} value={decl.partner_name} testid="verifier-partner" />
                  {item.brand && <Detail icon={<BadgeCheck size={16} />} label={t("hc.brand")} value={item.brand} testid="verifier-brand" />}
                  {item.color && <Detail icon={<Palette size={16} />} label={t("hc.color")} value={item.color} testid="verifier-color" />}
                  {item.condition && <Detail icon={<Sparkles size={16} />} label={t("hc.condition")} value={item.condition} testid="verifier-condition" />}
                  {item.deposit_date && <Detail icon={<CalendarDays size={16} />} label={t("hc.depositedOn")} value={fmtDate(item.deposit_date)} testid="verifier-date" />}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {fromPay ? (
                <Button onClick={() => navigate(`/pay/${declarationId}?verified=1`)}
                  className="rounded-full flex-1 h-12 text-base bg-findo-red hover:bg-findo-red/90 gap-2" data-testid="verifier-back-to-pay">
                  <ArrowLeft size={18} /> {t("hc.backToPayment")}
                </Button>
              ) : vstatus === "approved" ? (
                <Button onClick={proceedToPay} disabled={confirming} className="rounded-full flex-1 h-12 text-base bg-findo-red hover:bg-findo-red/90 gap-2" data-testid="verifier-proceed-pay">
                  {confirming ? <><FindoSpinner size={18} /> {t("hc.loading")}</> : <>{t("hc.continueToPayment")} <ArrowRight size={18} /></>}
                </Button>
              ) : (
                <>
                  <Button onClick={() => setVerifyOpen(true)} className="rounded-full flex-1 h-12 text-base bg-findo-red hover:bg-findo-red/90 gap-2" data-testid="verifier-confirm">
                    {t("hc.thisIsMyItemContinue")} <ArrowRight size={18} />
                  </Button>
                  {!alreadyPaid && !showReasons && (
                    <Button onClick={() => setShowReasons(true)} variant="outline" className="rounded-full flex-1 h-12 text-base gap-2" data-testid="verifier-reject">
                      <XCircle size={18} /> {t("hc.thisIsNotMyItem")}
                    </Button>
                  )}
                </>
              )}
            </div>

            {!fromPay && vstatus === "pending" && (
              <div className="mt-4"><WaitingNotice title={t("hc.verificationPending")} text={t("hc.theEstablishmentIsReviewingYour")} testid="verifier-verif-pending" /></div>
            )}
            {!fromPay && vstatus === "rejected" && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="verifier-verif-rejected">
                <p className="font-semibold text-red-700 flex items-center gap-1.5"><XCircle size={18} /> {t("hc.claimNotValidated")}</p>
                <p className="text-sm text-red-600 mt-1">{t("hc.theEstablishmentConsideredThatThis")}</p>
              </div>
            )}

            {!fromPay && showReasons && (
              <div className="mt-4 rounded-2xl border border-border bg-white p-5 card-shadow" data-testid="verifier-reasons">
                <p className="text-sm font-semibold text-findo-navy mb-1">{t("hc.whyIsThisNotYour")}</p>
                <p className="text-xs text-slate-500 mb-3">{t("hc.yourFeedbackHelpsUsRestart")}</p>
                <div className="flex flex-wrap gap-2">
                  {REJECT_REASONS.map((r) => (
                    <button key={r} onClick={() => (r === "Autre" ? setShowOther(true) : rejectMine(r))} disabled={rejecting}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                        r === "Autre" && showOther
                          ? "border-findo-navy bg-findo-navy/10 text-findo-navy"
                          : "border-findo-navy/20 text-findo-navy hover:bg-findo-navy/5 hover:border-findo-navy"}`}
                      data-testid={`verifier-reason-${r.replace(/[\s']+/g, '-').toLowerCase()}`}>
                      {rejecting && r !== "Autre" ? "…" : t(REASON_KEYS[r])}
                    </button>
                  ))}
                  <button onClick={() => { setShowReasons(false); setShowOther(false); setOtherText(""); }} disabled={rejecting}
                    className="rounded-full px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="verifier-reason-cancel">
                    {t("hc.cancel")}
                  </button>
                </div>

                {showOther && (
                  <div className="mt-4" data-testid="verifier-other-block">
                    <label className="text-sm font-semibold text-findo-navy">{t("hc.specifyYourReason")} <span className="text-findo-red">*</span></label>
                    <textarea value={otherText} onChange={(e) => setOtherText(e.target.value)} rows={3} maxLength={500}
                      placeholder={t("hc.explainWhyThisItemIs")}
                      className="mt-1.5 w-full rounded-xl border border-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-findo-navy/30 resize-none"
                      data-testid="verifier-other-text" autoFocus />
                    <Button onClick={() => rejectMine(otherText.trim())} disabled={rejecting || !otherText.trim()}
                      className="mt-2 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 gap-2" data-testid="verifier-other-submit">
                      {rejecting ? t("hc.sending") : t("hc.send")} <ArrowRight size={16} />
                    </Button>
                  </div>
                )}
              </div>
            )}

            <p className="mt-5 text-xs text-slate-400 flex items-center justify-center gap-1.5"><ShieldCheck size={13} /> {t("hc.secureFindoVerification")}</p>
          </>
        )}
      </div>

      <Dialog open={verifyOpen} onOpenChange={(o) => !o && setVerifyOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="verifier-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-findo-navy">{t("hc.identityVerification")}</DialogTitle>
          </DialogHeader>
          <VerificationAnswersForm questions={decl?.verification_questions} busy={verifying}
            onSubmit={submitVerification} onCancel={() => setVerifyOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy shrink-0">{icon}</span>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className="text-findo-navy font-medium">{value || "—"}</p>
      </div>
    </div>
  );
}
